import React, { useMemo, useState } from 'react';
import {
  Dialog,
  Pane,
  Heading,
  Text,
  Button,
  IconButton,
  CrossIcon,
  FileUploader,
  Table,
  Badge,
  Select,
  Alert,
  Spinner,
  TrashIcon,
} from 'evergreen-ui';

import { mutate } from 'swr';

import { useQueryCategories } from '../../services/api/categoriesService';
import { useQueryBanks } from '../../services/api/banksService';
import { postManyEntriesWithReconciliation } from '../../services/api/entriesService';
import { useQueryVitaUserByIdentityId } from '../../services/api/vitaUserService';
import { toastNotification } from '../../shared/toastNotification';
import {
  processSpreadsheet,
  buildEntryPayloads,
  applyBankAccountOverride,
  clearBankAccountOverride,
  applyBankAccount2Override,
  clearBankAccount2Override,
  applyCategoryOverride,
  clearCategoryOverride,
  removeRow,
  downloadImportTemplate,
} from '../../services/spreadsheetImportService';

const STEPS = {
  UPLOAD: 'upload',
  PREVIEW: 'preview',
  SUBMITTING: 'submitting',
  RESULT: 'result',
};

const PREVIEW_COLUMNS = [
  { key: 'linha', label: 'Linha', width: 60 },
  { key: 'validacao', label: 'Validação', width: 90 },
  { key: 'tipo', label: 'Tipo', width: 180 },
  { key: 'statusPagamento', label: 'Status', width: 90 },
  { key: 'competencia', label: 'Competência', width: 110 },
  { key: 'vencimento', label: 'Vencimento', width: 110 },
  { key: 'conta', label: 'Conta', width: 210 },
  { key: 'conta2', label: 'Conta 2', width: 210 },
  { key: 'categoria', label: 'Categoria', width: 210 },
  { key: 'descricao', label: 'Descrição', width: 220 },
  { key: 'valor', label: 'Valor', width: 110 },
  { key: 'acoes', label: 'Ações', width: 70 },
];

const PREVIEW_TABLE_MIN_WIDTH = PREVIEW_COLUMNS.reduce((sum, col) => sum + col.width, 0);

function ImportSpreadsheetModal({ isShown, setIsShown }) {
  const companyId = localStorage.getItem('companyId');
  const authUserId = localStorage.getItem('userId');

  // O backend espera o id interno da VitaUser (FK de JournalEntry), não o id de
  // identidade do provedor de autenticação armazenado em localStorage — mesmo
  // padrão usado por EntryFormComponent.js.
  const { data: vitaUser, isLoading: isLoadingVitaUser } = useQueryVitaUserByIdentityId(authUserId, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const userId = !isLoadingVitaUser ? vitaUser?.data?.id : undefined;

  const { data: categories = [], isLoading: isLoadingCategories } = useQueryCategories(companyId, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { data: banks = [], isLoading: isLoadingBanks } = useQueryBanks(companyId, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const [step, setStep] = useState(STEPS.UPLOAD);
  const [file, setFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [rows, setRows] = useState([]);
  const [resultMessage, setResultMessage] = useState('');
  const [resultIsError, setResultIsError] = useState(false);

  const bankList = !isLoadingBanks && banks?.data ? banks.data : [];
  const categoryList = !isLoadingCategories && categories?.data ? categories.data : [];

  const blockingCount = useMemo(() => rows.filter((r) => r.errors.length > 0).length, [rows]);
  const readyCount = rows.length - blockingCount;

  const resetAll = () => {
    setStep(STEPS.UPLOAD);
    setFile(null);
    setIsParsing(false);
    setParseError('');
    setRows([]);
    setResultMessage('');
    setResultIsError(false);
  };

  const onClose = () => {
    resetAll();
    setIsShown(false);
  };

  const handleFilesAccepted = async (files) => {
    const selected = files[0];
    setFile(selected);
    setParseError('');
    setIsParsing(true);

    try {
      const processedRows = await processSpreadsheet(selected, bankList, categoryList);
      setRows(processedRows);
      setStep(STEPS.PREVIEW);
    } catch (err) {
      console.log('Erro ao processar planilha', err);
      setParseError(err.message || 'Não foi possível ler o arquivo. Verifique o formato e tente novamente.');
    } finally {
      setIsParsing(false);
    }
  };

  const updateRow = (rowIndex, updater) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.rowIndex !== rowIndex) return row;
        const clone = { ...row, errors: [...row.errors], warnings: [...row.warnings] };
        return updater(clone);
      })
    );
  };

  const handleBankOverride = (rowIndex, bankId) => {
    updateRow(rowIndex, (row) => {
      if (!bankId) return clearBankAccountOverride(row);
      const bank = bankList.find((b) => b.id === bankId);
      return bank ? applyBankAccountOverride(row, bank) : row;
    });
  };

  const handleBank2Override = (rowIndex, bankId) => {
    updateRow(rowIndex, (row) => {
      if (!bankId) return clearBankAccount2Override(row);
      const bank = bankList.find((b) => b.id === bankId);
      return bank ? applyBankAccount2Override(row, bank) : row;
    });
  };

  const handleCategoryOverride = (rowIndex, categoryId) => {
    updateRow(rowIndex, (row) => {
      if (!categoryId) return clearCategoryOverride(row);
      const category = categoryList.find((c) => c.id === categoryId);
      return category ? applyCategoryOverride(row, category) : row;
    });
  };

  const handleRemoveRow = (rowIndex) => {
    setRows((prevRows) => {
      const clonedRows = prevRows.map((row) => ({ ...row, errors: [...row.errors] }));
      return removeRow(clonedRows, rowIndex);
    });
  };

  const handleSubmit = async () => {
    if (!userId) {
      toastNotification('warning', 'Não foi possível identificar o usuário. Recarregue a página e tente novamente.');
      return;
    }

    setStep(STEPS.SUBMITTING);

    const entries = buildEntryPayloads(rows, companyId, userId);

    try {
      const res = await postManyEntriesWithReconciliation(entries, [], {});
      const createdCount = res?.data?.data?.length ?? res?.data?.length ?? entries.length;

      mutate('entriesFilteredByDate');
      mutate((key) => typeof key === 'string' && key.startsWith('bankBalances-'));
      mutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-'));

      setResultIsError(false);
      setResultMessage(`${createdCount} lançamento(s) importado(s) com sucesso.`);
      setStep(STEPS.RESULT);
      toastNotification('success', 'Importação concluída com sucesso.');
    } catch (err) {
      console.log('Erro ao importar planilha', err);
      setResultIsError(true);
      setResultMessage(
        'Não foi possível concluir a importação. Nenhum lançamento foi criado. Verifique os dados e tente novamente.'
      );
      setStep(STEPS.RESULT);
    }
  };

  const renderUploadStep = () => (
    <Pane>
      <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
        <Text>Envie uma planilha .xlsx no formato padrão do Vita.</Text>
        <Button onClick={downloadImportTemplate}>Baixar modelo (.xlsx)</Button>
      </Pane>

      <FileUploader
        label="Planilha de lançamentos"
        description="Arraste o arquivo aqui ou clique para selecionar"
        browseOrDragText={() => 'Selecione ou arraste um arquivo aqui'}
        maxFiles={1}
        maxSizeInBytes={10 * 1024 * 1024}
        acceptedMimeTypes={[
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
        ]}
        values={file ? [file] : []}
        onAccepted={handleFilesAccepted}
        onRejected={() =>
          setParseError('Arquivo não suportado. Envie um arquivo .xlsx ou .xls de até 10MB.')
        }
        onRemove={() => {
          setFile(null);
          setParseError('');
        }}
      />

      {isParsing && (
        <Pane display="flex" alignItems="center" gap={8} marginTop={16}>
          <Spinner size={20} />
          <Text>Processando planilha...</Text>
        </Pane>
      )}

      {parseError && (
        <Alert intent="danger" title={parseError} marginTop={16} />
      )}
    </Pane>
  );

  // Badge de VALIDAÇÃO da linha (Ok/Aviso/Erro) — não confundir com a coluna
  // "Status" da própria planilha (Pago/Vencido), exibida separadamente abaixo.
  const renderValidationBadge = (row) => {
    if (row.errors.length > 0) {
      return <Badge color="red">Erro</Badge>;
    }
    if (row.warnings.length > 0) {
      return <Badge color="yellow">Aviso</Badge>;
    }
    return <Badge color="green">Ok</Badge>;
  };

  const renderStatusPagamentoCell = (row) => (
    <Badge color={row.statusPayment ? 'green' : 'neutral'}>
      {row.statusRaw || (row.statusPayment ? 'Pago' : 'Não pago')}
    </Badge>
  );

  // Conta, Conta 2 e Categoria ficam sempre editáveis (não só quando há erro),
  // pois a resolução automática por nome pode ter acertado a linha errada.
  const renderContaCell = (row) => (
    <Select
      width="100%"
      value={row.bankAccountId || ''}
      onChange={(e) => handleBankOverride(row.rowIndex, e.target.value)}
    >
      <option value="">Selecionar conta...</option>
      {bankList.map((bank) => (
        <option key={bank.id} value={bank.id}>
          {bank.name}
        </option>
      ))}
    </Select>
  );

  const renderCategoriaCell = (row) => {
    if (row.isTransferLeg) return <Text color="muted">—</Text>;

    const matchingCategories = categoryList.filter((c) => c.isInput === row.isInput && c.enabled);

    if (matchingCategories.length === 0) {
      return (
        <Text size={300} color="red600">
          {`Nenhuma categoria de ${row.isInput ? 'entrada' : 'saída'} cadastrada`}
        </Text>
      );
    }

    return (
      <Select
        width="100%"
        value={row.categoryId || ''}
        onChange={(e) => handleCategoryOverride(row.rowIndex, e.target.value)}
      >
        <option value="">Selecionar categoria...</option>
        {matchingCategories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </Select>
    );
  };

  const renderConta2Cell = (row) => {
    if (!row.isTransferLeg) return <Text color="muted">—</Text>;

    return (
      <Select
        width="100%"
        value={row.bankAccountId2 || ''}
        onChange={(e) => handleBank2Override(row.rowIndex, e.target.value)}
      >
        <option value="">Selecionar conta...</option>
        {bankList.map((bank) => (
          <option key={bank.id} value={bank.id}>
            {bank.name}
          </option>
        ))}
      </Select>
    );
  };

  const renderAcoesCell = (row) => (
    <IconButton
      icon={TrashIcon}
      intent="danger"
      appearance="minimal"
      onClick={() => handleRemoveRow(row.rowIndex)}
    />
  );

  // Um único lugar mapeando cada coluna ao seu conteúdo, para que header e
  // corpo nunca fiquem fora de sincronia ao adicionar/remover/reordenar colunas.
  const CELL_RENDERERS = {
    linha: (row) => row.rowIndex,
    validacao: (row) => renderValidationBadge(row),
    tipo: (row) => row.tipoRaw,
    statusPagamento: (row) => renderStatusPagamentoCell(row),
    competencia: (row) => row.dateCompetencia,
    vencimento: (row) => row.dateVencimento,
    conta: (row) => renderContaCell(row),
    conta2: (row) => renderConta2Cell(row),
    categoria: (row) => renderCategoriaCell(row),
    descricao: (row) => row.description,
    valor: (row) => row.valor,
    acoes: (row) => renderAcoesCell(row),
  };

  const TEXT_CELL_KEYS = new Set(['linha', 'tipo', 'competencia', 'vencimento', 'descricao', 'valor']);

  const renderPreviewStep = () => (
    <Pane>
      <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={12}>
        <Heading size={400}>
          {`${readyCount} de ${rows.length} linha(s) prontas para importar${
            blockingCount > 0 ? `, ${blockingCount} com erro` : ''
          }.`}
        </Heading>
        <Button onClick={resetAll}>Trocar arquivo</Button>
      </Pane>

      <Pane border="default">
        <Table minWidth={PREVIEW_TABLE_MIN_WIDTH}>
          <Table.Head>
            {PREVIEW_COLUMNS.map((col) => (
              <Table.TextHeaderCell
                key={col.key}
                flexBasis={col.width}
                width={col.width}
                flexShrink={0}
                flexGrow={0}
              >
                {col.label}
              </Table.TextHeaderCell>
            ))}
          </Table.Head>
          <Table.Body overflow="visible">
            {rows.map((row) => (
              <Table.Row key={row.rowIndex} height="auto" minHeight={48} paddingY={8}>
                {PREVIEW_COLUMNS.map((col) => {
                  const CellComponent = TEXT_CELL_KEYS.has(col.key) ? Table.TextCell : Table.Cell;
                  return (
                    <CellComponent
                      key={col.key}
                      flexBasis={col.width}
                      width={col.width}
                      flexShrink={0}
                      flexGrow={0}
                      isNumber={col.key === 'valor' ? true : undefined}
                    >
                      {CELL_RENDERERS[col.key](row)}
                    </CellComponent>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Pane>

      {rows.some((r) => r.errors.length > 0) && (
        <Pane marginTop={12}>
          {rows
            .filter((r) => r.errors.length > 0)
            .map((r) => (
              <Text key={r.rowIndex} display="block" size={300} color="red600">
                {`Linha ${r.rowIndex}: ${r.errors.join(' ')}`}
              </Text>
            ))}
        </Pane>
      )}

      {rows.some((r) => r.errors.length === 0 && r.warnings.length > 0) && (
        <Pane marginTop={12}>
          {rows
            .filter((r) => r.errors.length === 0 && r.warnings.length > 0)
            .map((r) => (
              <Text key={r.rowIndex} display="block" size={300} color="#946200">
                {`Linha ${r.rowIndex}: ${r.warnings.join(' ')}`}
              </Text>
            ))}
        </Pane>
      )}
    </Pane>
  );

  const renderResultStep = () => (
    <Pane>
      <Alert intent={resultIsError ? 'danger' : 'success'} title={resultMessage} />
      <Pane display="flex" gap={8} marginTop={16}>
        <Button onClick={resetAll}>Importar outra planilha</Button>
        <Button appearance="primary" onClick={onClose}>
          Fechar
        </Button>
      </Pane>
    </Pane>
  );

  const buildFooter = () => {
    if (step === STEPS.PREVIEW) {
      return (
        <Pane display="flex" justifyContent="flex-end" gap={8}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            appearance="primary"
            disabled={rows.length === 0 || blockingCount > 0 || !userId}
            onClick={handleSubmit}
          >
            Importar
          </Button>
        </Pane>
      );
    }
    return null;
  };

  return (
    <Dialog
      isShown={isShown}
      title=""
      onCloseComplete={onClose}
      hasFooter={step === STEPS.PREVIEW}
      header={() => (
        <Pane display="flex" flex={1} justifyContent="space-between" alignItems="center">
          <Heading size={500}>Importar planilha</Heading>
          <IconButton appearance="minimal" icon={CrossIcon} onClick={onClose} />
        </Pane>
      )}
      footer={buildFooter}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEscapePress={true}
      width={step === STEPS.PREVIEW ? 1300 : 720}
      backgroundColor="#f8f8f8"
    >
      {step === STEPS.UPLOAD && renderUploadStep()}
      {step === STEPS.PREVIEW && renderPreviewStep()}
      {step === STEPS.SUBMITTING && (
        <Pane display="flex" alignItems="center" gap={8}>
          <Spinner size={20} />
          <Text>Importando lançamentos...</Text>
        </Pane>
      )}
      {step === STEPS.RESULT && renderResultStep()}
    </Dialog>
  );
}

export default ImportSpreadsheetModal;
